import React, { useRef } from "react";

import { Document, Page, Text, View, Font } from "@react-pdf/renderer";

import { withNamespaces } from "react-i18next";

import { 
    FONT_OPEN_SANS_BOLD, 
    FONT_OPEN_SANS_REGULAR
} from "src/components/constants";

import DateUtils from "src/services/utils/DateUtils";

import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

import { now } from "moment";

import FirstPageHeader from "./components/FirstPageHeader";
import CoverPage from "./components/CoverPage";
import CoverPageHeader from "./components/CoverPageHeader";
import ManagementSummary from "./components/ManagementSummary";
import BasicCompanyInformation from "./components/BasicCompanyInformation";
import TableOfContents from "./components/TableOfContents";
import Footer from "./components/Footer";
import Header from "./components/Header";
import { PageReferencesProvider } from "./components/PageReference";
import Sections from "./Sections";
import styles from "./Styles";
import {
    resolveBranding,
    resolveLogo,
} from "./helpers";

Font.register({
    family: "Open Sans",
    fonts: [{
            src: FONT_OPEN_SANS_REGULAR,
            fontWeight: 400,
            fontStyle: "normal",
        },
        {
            src: FONT_OPEN_SANS_REGULAR,
            fontWeight: 400,
            fontStyle: "italic",
        },
        {
            src: FONT_OPEN_SANS_BOLD,
            fontWeight: 600,
            fontStyle: "normal",
        },
        {
            src: FONT_OPEN_SANS_BOLD,
            fontWeight: 600,
            fontStyle: "italic",
        },
    ],
});

const Supplier = (props) => {
	const { 
        t,
        organization,
        supplierData,
        user,
        downloadDate,
        overviewHeader,
        statistics,
        lastActivities,
        riskMatrixData,
    } = props;

    const pageReferencesRef = useRef(new Map());

	if (!supplierData) {
		return (
			<Document>
				<Page size="A4" style={styles.page1}></Page>
			</Document>
		);
	}

    const { primaryColor, stopColor, sectionHeaderColor } = resolveBranding(organization);
    const logo = resolveLogo(organization);
    const isOwnCompany = !!supplierData.supplierDetail?.isOwnCompany;
	
    const dateUtils = new DateUtils();
    const reportDownloadDate = downloadDate
        || dateUtils.convertDateToDate(now(), INTERNATIONAL_DATE_FORMAT);

    const renderGeneralInfosSection = () => {
        return <BasicCompanyInformation supplierData={supplierData} />;
    };

    styles.sectionSeparator.backgroundColor = sectionHeaderColor;

    return (
        <PageReferencesProvider pageReferences={pageReferencesRef}>
        <Document>
            <Page size="A4" style={styles.page}>
                <CoverPage
                    supplierDetail={{
                        ...(supplierData.supplierDetail || {}),
                        connectedLegalEntities: supplierData.connectedLegalEntities || [],
                    }}
                    user={user}
                    downloadDate={reportDownloadDate}
                    logo={logo}
                    primaryColor={primaryColor}
                />

                <Footer backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <CoverPageHeader
                    backgroundColor={primaryColor}
                    logo={logo}
                />

                <ManagementSummary
                    t={t}
                    supplierData={supplierData}
                    overviewHeader={overviewHeader}
                    statistics={statistics}
                    lastActivities={lastActivities}
                    riskMatrixData={riskMatrixData}
                    isOwnCompany={isOwnCompany}
                />

                <Footer backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <FirstPageHeader 
                    startColor={primaryColor} 
                    stopColor={stopColor} 
                    backgroundColor={primaryColor} 
                    logo={logo} 
                    isOwnCompany={isOwnCompany}
                />

                <TableOfContents />

                <Footer backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <FirstPageHeader 
                    startColor={primaryColor} 
                    stopColor={stopColor} 
                    backgroundColor={primaryColor} 
                    logo={logo} 
                    isOwnCompany={isOwnCompany}
                />

                <View>
                    {renderGeneralInfosSection()}
                </View>

                <Footer backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <Header 
                    startColor={primaryColor} 
                    stopColor={stopColor} 
                    backgroundColor={primaryColor} 
                    logo={logo} 
                />

                <View>
                    <Sections 
                        supplierData={supplierData}
                        noteBackgroundColor={styles.sectionSeparator.backgroundColor}
                        user={user}
                        downloadDate={reportDownloadDate}
                    />
                </View>

                <Footer backgroundColor={primaryColor} />
            </Page>
        </Document>
        </PageReferencesProvider>
    );
      
};


export default withNamespaces()(
    Supplier
);
